/**
 * lcoeBasis — the vocabulary for WHICH levelised cost a number is.
 *
 * WHY THIS EXISTS. Four different LCOE definitions print under one label across
 * this codebase, and until now nothing on the wire said which one a reader was
 * looking at:
 *
 *  - the PROJECT-FINANCE figure (`projectFinance.computeLcoeUsdPerMwh`) — a
 *    capital-recovery-factor levelisation of THIS project's own declared CAPEX,
 *    OPEX and financing;
 *  - the PHASED blend of that same engine across a staged build;
 *  - the BENCHMARK-PATHWAY figure (`finDecision/pathwayLcoe`) — what a
 *    technology OPTION of this size would cost, from preset economics, on
 *    escalated flows discounted at a real rate;
 *  - the SIMULATED figure, levelised over an EO annual-yield run's energy.
 *
 * They answer different questions and legitimately differ, often by a lot. The
 * defect was never that they exist — it is that they were rendered
 * interchangeably, so a reader comparing two screens could not tell whether a
 * $40/MWh gap meant a finding or a change of subject.
 *
 * DECISION (audit §5 Q6): the PROJECT-FINANCE figure is the report HEADLINE;
 * the pathway figure is always labelled as a benchmark; the EO figure is always
 * labelled as simulated. This module is the single place those names live, so a
 * surface cannot invent its own wording for a basis.
 */

/** Which levelised-cost definition a figure was computed on. */
export type LcoeBasis =
  /** This project's declared CAPEX / OPEX / financing (the report headline). */
  | 'project-finance'
  /** The same engine, blended across a staged build's phases. */
  | 'project-finance-phased'
  /** A benchmark technology option priced from preset economics. */
  | 'benchmark-pathway'
  /** Levelised over a simulated annual-yield run's delivered energy. */
  | 'simulated-yield';

/** Short label for a chip or a column header. */
export function lcoeBasisLabel(basis: LcoeBasis): string {
  switch (basis) {
    case 'project-finance': return 'project basis';
    case 'project-finance-phased': return 'project basis (phased)';
    case 'benchmark-pathway': return 'benchmark pathway';
    case 'simulated-yield': return 'simulated';
  }
}

/** One sentence a reader can act on, for a caption or a methodology note. */
export function lcoeBasisNote(basis: LcoeBasis): string {
  switch (basis) {
    case 'project-finance':
      return 'Levelised from this project’s own declared CAPEX, OPEX and financing.';
    case 'project-finance-phased':
      return 'Levelised from this project’s declared inputs, energy-weighted across its phases.';
    case 'benchmark-pathway':
      return 'What a benchmark technology option of this size would cost – not a figure for this project’s declared inputs.';
    case 'simulated-yield':
      return 'Levelised over a simulated annual-yield run rather than a declared capacity factor.';
  }
}

/**
 * Do two figures answer the SAME question?
 *
 * A difference between two bases is a change of subject, not a discrepancy, so
 * callers must not present it as a reconciliation error. The phased blend and
 * the single-phase figure ARE the same basis at different granularity, and are
 * therefore genuinely comparable.
 */
export function lcoeBasesAreComparable(a: LcoeBasis, b: LcoeBasis): boolean {
  const projectSide = (x: LcoeBasis): boolean =>
    x === 'project-finance' || x === 'project-finance-phased';
  if (projectSide(a) && projectSide(b)) return true;
  return a === b;
}
